import React, { useState, useMemo, useEffect } from "react";
import mosques from "../mosques.json";

const IslamicServiceMosque = () => {
  const [search, setSearch] = useState("");
  const [district, setDistrict] = useState("");
  const [taluka, setTaluka] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedMosque, setSelectedMosque] = useState(null);

  const mosquesPerPage = 6;

  // Reset page when filter/search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [search, district, taluka]);

  // Unique districts (safe)
  const districts = useMemo(() => {
    return [
      ...new Set(
        mosques
          .map((m) => m.district)
          .filter((d) => d && d.trim() !== "")
      ),
    ];
  }, []);

  // Unique talukas (safe)
  const talukas = useMemo(() => {
    return [
      ...new Set(
        mosques
          .map((m) => m.taluka)
          .filter((t) => t && t.trim() !== "")
      ),
    ];
  }, []);

  // Filtering logic (safe version)
  const filteredMosques = useMemo(() => {
    return mosques.filter((mosque) => {
      const name = mosque.name || "";
      const mosqueDistrict = mosque.district || "";
      const mosqueTaluka = mosque.taluka || "";

      return (
        name.toLowerCase().includes(search.toLowerCase()) &&
        (district === "" || mosqueDistrict === district) &&
        (taluka === "" || mosqueTaluka === taluka)
      );
    });
  }, [search, district, taluka]);

  // Pagination logic
  const totalPages = Math.ceil(filteredMosques.length / mosquesPerPage);
  const indexOfLast = currentPage * mosquesPerPage;
  const indexOfFirst = indexOfLast - mosquesPerPage;
  const currentMosques = filteredMosques.slice(indexOfFirst, indexOfLast);

  return (
    <div className="container my-5">
      <h2 className="display-5 fw-bold text-center mb-4">
        🕌 Ahel-e-Sunnah Mosque Directory
      </h2>

      {/* Filters */}
      <div className="row mb-4">
        <div className="col-md-4 mb-2">
          <input
            type="text"
            className="form-control"
            placeholder="🔍 Search Mosque"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="col-md-4 mb-2">
          <select
            className="form-select"
            value={district}
            onChange={(e) => setDistrict(e.target.value)}
          >
            <option value="">📍 All Districts</option>
            {districts.map((d, i) => (
              <option key={i} value={d}>
                {d}
              </option>
            ))}
          </select>
        </div>

        <div className="col-md-4 mb-2">
          <select
            className="form-select"
            value={taluka}
            onChange={(e) => setTaluka(e.target.value)}
          >
            <option value="">🏷 All Talukas</option>
            {talukas.map((t, i) => (
              <option key={i} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Results Count */}
      <p className="text-muted">
        Showing {currentMosques.length} of {filteredMosques.length} mosques
      </p>

      {/* Mosque Cards */}
      <div className="row">
        {currentMosques.length > 0 ? (
          currentMosques.map((mosque, index) => (
            <div key={index} className="col-md-6 col-lg-4 mb-4">
              <div className="card shadow-sm h-100">
                <div className="card-body">
                  <h5 className="fw-bold text-success">
                    {mosque.name || "N/A"}
                  </h5>
                  <p className="small mb-1">📍 {mosque.address || "-"}</p>
                  <p className="small mb-2">🏙 {mosque.district || "-"}</p>

                  <button
                    className="btn btn-outline-primary btn-sm w-100 mb-2"
                    onClick={() => setSelectedMosque(mosque)}
                  >
                    View Details
                  </button>

                  {mosque.trustyContact &&
                    mosque.trustyContact !== "-" && (
                      <a
                        href={`https://wa.me/91${mosque.trustyContact}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-success btn-sm w-100 mb-2"
                      >
                        💬 WhatsApp
                      </a>
                    )}

                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                      mosque.address || ""
                    )}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-dark btn-sm w-100"
                  >
                    🗺 View on Map
                  </a>
                </div>
              </div>
            </div>
          ))
        ) : (
          <p className="text-center text-muted">No mosques found.</p>
        )}
      </div>

      {/* Smart Pagination */}
      {totalPages > 1 && (
        <div className="d-flex justify-content-center align-items-center mt-4 flex-wrap">
          <button
            className="btn btn-outline-success mx-1"
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(currentPage - 1)}
          >
            ◀ Prev
          </button>

          {Array.from({ length: totalPages }, (_, i) => i + 1)
            .filter(
              (page) =>
                page >= currentPage - 2 &&
                page <= currentPage + 2
            )
            .map((page) => (
              <button
                key={page}
                className={`btn mx-1 ${
                  currentPage === page
                    ? "btn-success"
                    : "btn-outline-success"
                }`}
                onClick={() => setCurrentPage(page)}
              >
                {page}
              </button>
            ))}

          <button
            className="btn btn-outline-success mx-1"
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage(currentPage + 1)}
          >
            Next ▶
          </button>
        </div>
      )}

      {/* Modal */}
      {selectedMosque && (
        <div
          className="modal d-block"
          tabIndex="-1"
          style={{ background: "rgba(0,0,0,0.6)" }}
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {selectedMosque.name}
                </h5>
                <button
                  className="btn-close"
                  onClick={() => setSelectedMosque(null)}
                ></button>
              </div>
              <div className="modal-body">
                <p><strong>Address:</strong> {selectedMosque.address}</p>
                <p><strong>District:</strong> {selectedMosque.district}</p>
                <p><strong>Taluka:</strong> {selectedMosque.taluka}</p>
                <p><strong>Trusty:</strong> {selectedMosque.trusty}</p>
                <p><strong>Trusty Contact:</strong> {selectedMosque.trustyContact}</p>
                <p><strong>Imam:</strong> {selectedMosque.imam}</p>
                <p><strong>Imam Contact:</strong> {selectedMosque.imamContact}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IslamicServiceMosque;